<script setup lang="ts">
import Mathematics from '@tiptap/extension-mathematics';
import StarterKit from '@tiptap/starter-kit';
import { Editor, EditorContent } from '@tiptap/vue-3';
import 'katex/dist/katex.min.css';
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue';
import { $t } from '@teachino/shared-i18n';
import ModalV2DefaultFooter from '../ModalV2/ModalV2DefaultFooter.vue';
import ModalV2Layout from '../ModalV2/ModalV2Layout.vue';
import TFormField from '../Form/TFormField.vue';
import type { FormulaDialogProps } from './formula-dialog';
import { formulaCheatSheet } from './formula-dialog';

const props = defineProps<FormulaDialogProps>();

const input = useTemplateRef<HTMLTextAreaElement>('input');

onMounted(() => {
  input.value?.focus();
});

const latexModel = ref(props.initialLatex ?? '');

const readonlyEditor = new Editor({
  extensions: [StarterKit, Mathematics],
  editable: false,
});

watch(
  latexModel,
  () => {
    readonlyEditor.commands.clearContent();
    readonlyEditor.commands.insertInlineMath({ latex: latexModel.value });
  },
  { immediate: true },
);

const showAllTips = ref(false);
const defaultNumberOfTips = 3;
const displayedTips = computed(() =>
  showAllTips.value
    ? formulaCheatSheet
    : formulaCheatSheet.slice(0, defaultNumberOfTips),
);

const resolveSave = () => {
  props.resolve({ cancelled: false, value: latexModel.value.trim() });
};

const resolveCancel = () => {
  props.resolve({ cancelled: true, value: '' });
};

onBeforeUnmount(() => {
  readonlyEditor.destroy();
});
</script>

<template>
  <ModalV2Layout>
    <template #content>
      <div class="w-full">
        <TFormField>
          <template #label>
            <div class="mb-2">{{ $t('katex.enter_equation') }}</div>
          </template>
          <textarea
            ref="input"
            v-model="latexModel"
            rows="2"
            class="focus:border-primary-500 focus:ring-primary-200 w-full rounded-md border border-neutral-300 p-2 text-lg focus:ring-2 focus:outline-none"
            type="text"
          />
        </TFormField>

        <div class="mt-4 rounded-md bg-neutral-100 p-4">
          <span class="font-bold">{{ $t('katex.preview') }}</span>
          <div v-if="!latexModel" class="mt-2 text-sm text-neutral-500">
            {{ $t('katex.preview.empty') }}
          </div>
          <EditorContent :editor="readonlyEditor" />
        </div>

        <div class="mt-8">
          <table class="w-full text-sm text-neutral-500">
            <tbody>
              <tr
                v-for="(item, index) in displayedTips"
                :key="index"
                class="border-b border-neutral-200 hover:bg-neutral-50"
              >
                <td class="py-2 pr-4">
                  {{ $t(item.stringIdentifier) }}
                </td>
                <td class="py-2 font-mono text-sm">
                  {{ item.value }}
                </td>
              </tr>
            </tbody>
          </table>
          <button
            type="button"
            class="text-primary-500 mt-2 text-sm hover:underline"
            @click="showAllTips = !showAllTips"
          >
            {{
              showAllTips
                ? $t('katex.hide_all_tips')
                : $t('katex.show_all_tips')
            }}
          </button>
        </div>
      </div>
    </template>

    <template #footer>
      <ModalV2DefaultFooter
        :action-button-label="$t('generic_save')"
        :cancel-button-label="$t('generic_cancel')"
        @on-action="resolveSave"
        @on-cancel="resolveCancel"
      />
    </template>
  </ModalV2Layout>
</template>
